import type { Food } from "@corpuscomplete/core/nutrition/food";
import { NumericTarget } from "@corpuscomplete/core/common/numeric-target";
import type { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { Nutrient as NutrientDomain } from "@corpuscomplete/core/nutrition/nutrient";
import type { Result } from "@corpuscomplete/core/utils";
import { queries } from "@corpuscomplete/core/zero";
import { useQuery } from "@corpuscomplete/zero-data-transformer/react";
import { useMemo } from "react";

export namespace NutrientUI {
  type FoodItems = {
    items: Food.Item[];
    amounts?: undefined;
  };

  type NutrientAmounts = {
    amounts: Nutrient.Amounts;
    items?: Food.Item[];
  };

  const NUTRIENT_KEYS: Nutrient.Key[] = ["energy", "carbohydrate", "protein", "fat"];

  export type TargetInputs = Partial<Record<Nutrient.Key, string>>;

  export type TargetInputParseErrorCode = "invalidNonNegativeNumber";

  export type TargetInputParseError = Partial<Record<Nutrient.Key, TargetInputParseErrorCode>>;

  export type NutrientValues = FoodItems | NutrientAmounts;

  type BaseProps = {
    className?: string;
    fractionDigits?: number;
    abbreviated?: boolean;
    textMuted?: boolean;
    hasIncompleteData?: boolean;
  };

  export type NutrientAmountsLabelProps = BaseProps & NutrientValues;

  export type NutrientTargetsLabelProps = BaseProps & {
    targets: Nutrient.Targets;
  };

  export type NutrientsProgressProps = {
    className?: string;
    fractionDigits?: number;
    targets: Nutrient.Targets;
    actual: NutrientValues;
  };

  export function useDisplayNutrients() {
    const [nutrients] = useQuery(queries.nutrient.byKeys(NUTRIENT_KEYS));

    return useMemo(() => {
      const sorted: Nutrient.Info[] = [];
      for (const key of NUTRIENT_KEYS) {
        const nutrient = nutrients.find(({ key: nutrientKey }) => nutrientKey === key);
        if (nutrient) {
          sorted.push(nutrient);
        }
      }
      return {
        nutrients: sorted,
        nutrientKeys: NUTRIENT_KEYS,
      };
    }, [nutrients]);
  }

  export function nutrientValuesAsAmounts(values: NutrientValues, _nutrients: Nutrient.Info[]) {
    if (values.amounts) {
      return NutrientDomain.addFoodItems(values.amounts, values.items ?? []);
    } else {
      return NutrientDomain.addFoodItems(NutrientDomain.emptyAmounts(), values.items);
    }
  }

  export function nutrientLabel(nutrient: Nutrient.Info, abbreviated: boolean) {
    if (nutrient.key === "energy") {
      if (abbreviated) {
        return "";
      } else {
        return "Energy: ";
      }
    }

    let text = nutrient.defaultName;
    if (abbreviated) {
      text = text.charAt(0).toUpperCase();
    }
    return `${text}: `;
  }

  export function formatTarget(
    target: Nutrient.Targets[Nutrient.Key] | undefined,
    unit: string | undefined,
    fractionDigits: number,
  ): string | null {
    if (!target) {
      return null;
    }

    return NumericTarget.format(target, { unitName: unit, fractionDigits });
  }

  export function targetsToInputs(
    targets: Nutrient.Targets | null | undefined,
    keys: readonly Nutrient.Key[] = NutrientDomain.macroEnergyKeys,
  ): TargetInputs {
    const inputs: TargetInputs = {};
    for (const key of keys) {
      const target = targets?.[key];
      if (target?.type === "exact") {
        inputs[key] = target.value.toString();
      }
    }
    return inputs;
  }

  export function inputsAreBlank(
    inputs: TargetInputs,
    keys: readonly Nutrient.Key[] = NutrientDomain.macroEnergyKeys,
  ): boolean {
    return keys.every((key) => (inputs[key] ?? "").trim() === "");
  }

  export function parseTargetInputs(
    inputs: TargetInputs,
  ): Result<Nutrient.Targets | null, TargetInputParseError> {
    const targets: Nutrient.Targets = {};
    const errors: TargetInputParseError = {};

    for (const [key, input] of Object.entries(inputs) as [Nutrient.Key, string | undefined][]) {
      const value = input?.trim() ?? "";
      if (value === "") {
        continue;
      }
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || parsed < 0) {
        errors[key] = "invalidNonNegativeNumber";
        continue;
      }
      targets[key] = { type: "exact", value: parsed };
    }

    if (Object.keys(errors).length > 0) {
      return { ok: false, error: errors };
    }

    return { ok: true, value: Object.keys(targets).length === 0 ? null : targets };
  }

  export function mergeExactTargetInputs({
    existingTargets,
    inputs,
    keys = NutrientDomain.macroEnergyKeys,
  }: {
    existingTargets: Nutrient.Targets | null | undefined;
    inputs: TargetInputs;
    keys?: readonly Nutrient.Key[];
  }): Result<Nutrient.Targets | null, TargetInputParseError> {
    const parsed = parseTargetInputs(inputs);
    if (!parsed.ok) {
      return parsed;
    }

    const targets: Nutrient.Targets = { ...existingTargets };
    for (const key of keys) {
      const input = inputs[key]?.trim() ?? "";
      if (input === "") {
        delete targets[key];
      }
    }

    if (parsed.value) {
      for (const [key, target] of Object.entries(parsed.value) as [
        Nutrient.Key,
        Nutrient.Targets[Nutrient.Key],
      ][]) {
        targets[key] = target;
      }
    }

    return { ok: true, value: Object.keys(targets).length === 0 ? null : targets };
  }

  export function scaleTargetsToInputs(
    targets: Nutrient.Targets,
    factor: number,
    keys: readonly Nutrient.Key[] = NutrientDomain.macroEnergyKeys,
  ): TargetInputs {
    const inputs: TargetInputs = {};
    for (const key of keys) {
      const target = targets[key];
      if (target?.type === "exact") {
        inputs[key] = Math.round(target.value * factor).toString();
      }
    }
    return inputs;
  }

  export function sumTargets(targets: readonly Nutrient.Targets[]): Nutrient.Targets {
    const result: Nutrient.Targets = {};
    for (const targetSet of targets) {
      for (const [key, target] of Object.entries(targetSet) as [
        Nutrient.Key,
        Nutrient.Targets[Nutrient.Key],
      ][]) {
        if (target?.type !== "exact") {
          continue;
        }
        const existing = result[key];
        result[key] = {
          type: "exact",
          value: (existing?.type === "exact" ? existing.value : 0) + target.value,
        };
      }
    }
    return result;
  }
}
